import { getErrorMessage } from "@/lib/api/api.client";
import { useHasRole } from "@/lib/auth/auth.roles";
import {
  useAuthGroups,
  type FronteggGroup,
} from "@/lib/auth/groups/groups.api";
import {
  GroupsFormDialog,
  GroupsFormDialogContent,
} from "@/lib/auth/groups/groups.form-dialog";
import Button from "@/lib/components/ui/button/button";
import Text from "@/lib/components/ui/text/text";
import { cn } from "@/lib/utils";
import { Check, Plus } from "lucide-react";
import { useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";

export type ScopeCardProps = {
  selected: boolean;
  onSelect: () => void;
  icon: ReactNode;
  title: string;
  description: string;
  /** When the card sits inside a bordered container, drop its own border. */
  wrapped?: boolean;
  /** When the user may not switch to this scope. */
  disabled?: boolean;
};

// the persönlich/organisation chooser, shared by the wizard and module settings
export function ScopeCard({
  selected,
  onSelect,
  icon,
  title,
  description,
  wrapped = false,
  disabled = false,
}: ScopeCardProps) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      disabled={disabled}
      onClick={onSelect}
      className={cn(
        "flex w-full items-center gap-3 p-4 text-left transition-colors",
        wrapped
          ? "rounded-lg"
          : cn(
              "rounded-lg border",
              selected
                ? "border-primary-500 bg-background-neutral"
                : "border-border hover:bg-background-neutral",
            ),
        disabled && "opacity-disabled hover:bg-transparent",
      )}
    >
      <span className="text-foreground-pressed flex size-10 shrink-0 items-center justify-center">
        {icon}
      </span>
      <span className="flex min-w-0 flex-col">
        <Text weight="semibold" variant="label">
          {title}
        </Text>
        <Text size="sm" variant="label" className="text-foreground-pressed">
          {description}
        </Text>
      </span>
      <span
        aria-hidden
        className={cn(
          "ml-auto flex size-5 shrink-0 items-center justify-center rounded-full border-2",
          selected ? "border-primary-500" : "border-border",
        )}
      >
        {selected ? (
          <span className="bg-primary-500 size-2 rounded-full" />
        ) : null}
      </span>
    </button>
  );
}

export type GroupRestrictionProps = {
  onReset: () => void;
  disabled?: boolean;
  onToggle: (groupId: string) => void;
  selectedIds: string[];
  onGroupCreated: (groupId: string) => void;
};

// pick which org groups may see the module; empty means everyone in the org
export function GroupRestriction({
  onReset,
  disabled = false,
  onToggle,
  selectedIds,
  onGroupCreated,
}: GroupRestrictionProps) {
  const { t } = useTranslation();
  const isAdmin = useHasRole("Admin");
  const isSuperAdmin = useHasRole("SuperAdmin");
  const canCreateGroup = isAdmin || isSuperAdmin;
  const { data: groups, error, isLoading } = useAuthGroups();
  const [createOpen, setCreateOpen] = useState(false);

  const count = selectedIds.length;

  function openCreateDialog() {
    setCreateOpen(true);
  }

  function handleCreateOpenChange(open: boolean) {
    setCreateOpen(open);
  }

  function handleGroupCreated(groupId: string) {
    onGroupCreated(groupId);
  }

  return (
    <>
      <div
        className={cn(
          "flex flex-col gap-3",
          disabled && "opacity-disabled pointer-events-none select-none",
        )}
        aria-disabled={disabled || undefined}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex flex-col gap-0.5">
            <Text weight="medium" variant="label">
              {t("assistant_dialog_v2_access_groups_restrict_label")}
            </Text>
            <Text size="sm" variant="label" className="text-foreground-pressed">
              {count === 0
                ? t("assistant_dialog_v2_access_groups_hint_everyone")
                : t("assistant_dialog_v2_access_groups_hint_count", { count })}
            </Text>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {count > 0 ? (
              <button
                type="button"
                disabled={disabled}
                onClick={onReset}
                className="border-border text-foreground-pressed hover:bg-background-neutral inline-flex h-8 items-center rounded-full border px-3 text-sm transition-colors"
              >
                {t("assistant_dialog_v2_access_groups_reset")}
              </button>
            ) : null}
            {canCreateGroup && groups && groups.length > 0 ? (
              <Button
                size="sm"
                type="button"
                variant="outline"
                disabled={disabled}
                onClick={openCreateDialog}
              >
                <Plus />
                {t("assistant_dialog_v2_access_groups_create")}
              </Button>
            ) : null}
          </div>
        </div>

        {isLoading ? (
          <Text size="sm" className="text-foreground-pressed">
            {t("assistant_dialog_v2_basics_groups_loading")}
          </Text>
        ) : error ? (
          <Text size="sm" className="text-border-critical">
            {getErrorMessage(error)}
          </Text>
        ) : !groups || groups.length === 0 ? (
          <div className="flex flex-col items-start gap-3">
            <Text size="sm">
              {t(
                canCreateGroup
                  ? "assistant_dialog_v2_access_groups_empty"
                  : "assistant_dialog_v2_basics_groups_empty",
              )}
            </Text>
            {canCreateGroup ? (
              <Button
                size="sm"
                type="button"
                variant="outline"
                disabled={disabled}
                onClick={openCreateDialog}
              >
                <Plus />
                {t("assistant_dialog_v2_access_groups_create")}
              </Button>
            ) : null}
          </div>
        ) : (
          <div className="flex flex-wrap gap-2">
            {groups.map((group) => (
              <GroupPill
                key={group.id}
                group={group}
                active={selectedIds.includes(group.id)}
                disabled={disabled}
                onToggle={() => onToggle(group.id)}
              />
            ))}
          </div>
        )}
      </div>

      <GroupsFormDialog open={createOpen} onOpenChange={handleCreateOpenChange}>
        <GroupsFormDialogContent mode="create" onCreated={handleGroupCreated} />
      </GroupsFormDialog>
    </>
  );
}

function GroupPill({
  group,
  active,
  disabled = false,
  onToggle,
}: {
  group: FronteggGroup;
  active: boolean;
  disabled?: boolean;
  onToggle: () => void;
}) {
  const { t } = useTranslation();

  return (
    <button
      type="button"
      aria-pressed={active}
      disabled={disabled}
      aria-label={t("assistant_dialog_v2_basics_groups_card_aria", {
        name: group.name,
      })}
      onClick={onToggle}
      className={cn(
        "inline-flex h-8 items-center gap-1.5 rounded-full border px-3 text-sm transition-colors",
        active
          ? "border-transparent bg-background-primary font-medium text-foreground-secondary"
          : "border-border text-foreground-pressed hover:bg-background-neutral",
      )}
    >
      {active ? <Check className="size-3.5" /> : null}
      {group.name}
    </button>
  );
}
